/**
 * TA v4.3 — Scenario renderer.
 *
 * Renders question.scenario text. If it contains a triple-backtick fence (```java, ```sql,
 * ```json, ```log, etc.), the fenced block is rendered in a read-only Monaco editor with
 * the matching language. Plain prose is rendered as formatted text — NOT Monaco.
 *
 * Falls back to the assessment's declared metadata.primary_language only when there is a
 * fenced block with no language tag.
 */
import React from "react";
import Editor from "@monaco-editor/react";
import { splitFencedSegments, normalizeMonacoLanguage } from "./taHelpers";

const READ_ONLY_OPTIONS = {
    readOnly: true,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    fontSize: 13,
    fontFamily: "JetBrains Mono, ui-monospace, monospace",
    lineNumbers: "on",
    folding: false,
    glyphMargin: false,
    renderLineHighlight: "none",
    overviewRulerLanes: 0,
    scrollbar: { vertical: "hidden", horizontal: "auto", alwaysConsumeMouseWheel: false },
    contextmenu: false,
    automaticLayout: true,
    wordWrap: "on",
};

// Approx height = lines * lineHeight (18px) + small chrome
function calcHeight(code) {
    const lines = String(code || "").split("\n").length;
    return Math.min(Math.max(lines * 19 + 24, 80), 420);
}

function Prose({ text }) {
    if (!text) return null;
    return (
        <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{text}</p>
    );
}

export default function TAScenarioRenderer({ scenario, fallbackLanguage }) {
    const segments = splitFencedSegments(scenario);
    if (segments.length === 0) {
        return <Prose text={scenario} />;
    }
    return (
        <div className="space-y-3">
            {segments.map((seg, i) => {
                if (seg.kind === "prose") return <Prose key={i} text={seg.text} />;
                const lang = normalizeMonacoLanguage(seg.language || fallbackLanguage, "plaintext");
                return (
                    <div key={i} className="border border-border/40 bg-[#0f0f0f]" data-testid="ta-monaco-readonly">
                        <div className="flex items-center justify-between border-b border-border/40 bg-[#0a0a0a] px-3 py-1.5">
                            <span className="text-[10px] uppercase tracking-wider font-mono text-gray-500">{lang}</span>
                            <span className="text-[10px] font-mono text-gray-600">read-only</span>
                        </div>
                        <Editor
                            height={calcHeight(seg.code)}
                            language={lang}
                            value={seg.code}
                            theme="vs-dark"
                            options={READ_ONLY_OPTIONS}
                        />
                    </div>
                );
            })}
        </div>
    );
}
